import {useMemo} from 'react'
import {graphql, useSubscription} from 'react-relay'

import type {useRecentAgentActivitySubscription as UseRecentAgentActivitySubscriptionOp} from './__generated__/useRecentAgentActivitySubscription.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const recentAgentActivitySubscription = graphql`
  subscription useRecentAgentActivitySubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      updateIntentChanged {
        ...useRecentAgentActivity_issue
      }
    }
  }
`

/**
 * Subscribes to real-time pending agent suggestion updates for an issue.
 *
 * Subscribes to suggestion-specific issue update payloads so only pending
 * suggestion changes refresh this panel, keeping subscription updates scoped.
 *
 * The `issueUpdated` GraphQL subscription requires an authenticated viewer and
 * responds with FORBIDDEN for anonymous users. Callers MUST gate this hook (or
 * `RecentAgentActivitySubscriber` below) with `isLoggedIn()` so anonymous
 * visitors to public issue pages don't open a subscription the server will
 * refuse — otherwise the fetch-graphql layer throws "Expected data property in
 * response" and reports to failbot. See github/issues#22981.
 */
export function useRecentAgentActivitySubscription(issueId: string) {
  const config = useMemo(
    () => ({
      subscription: recentAgentActivitySubscription,
      variables: {issueId},
    }),
    [issueId],
  )

  useSubscription<UseRecentAgentActivitySubscriptionOp>(config)
}

/**
 * Render-null component that opens the `useRecentAgentActivitySubscription`.
 *
 * Mount this conditionally (e.g. behind `isLoggedIn()`) so that the underlying
 * hook only runs when the viewer is authenticated. Wrapping the hook in a
 * component lets callers gate the subscription in JSX without violating the
 * react-hooks/rules-of-hooks rule.
 */
export function RecentAgentActivitySubscriber({issueId}: {issueId: string}) {
  useRecentAgentActivitySubscription(issueId)
  return null
}
